import { GetServerSideProps } from 'next';
import { useParams } from 'next/navigation';
import { useContext, useEffect } from 'react';

import PacksList from '@/components/app/cards/PacksList';
import { PacksContext } from '@/components/app/layout/PacksProvider';
import Layout from '@/components/layout/LayoutWrapper';
import { usePacksFeed } from '@/hooks/app/usePacksFeed';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

export const customFeedNames: Record<string, string> = {
	work: 'collection-calm-for-work',
};

const PackFeed = () => {
	const params = useParams();
	const routerPush = useRouterPush();
	const feedQueryParam = typeof params?.feedName === 'string' ? params.feedName : '';

	const feedName = customFeedNames[feedQueryParam] ?? feedQueryParam;

	const { packs, tags, isLoading, title: feedTitle, error, hasResponse, notFound } = usePacksFeed(feedName);
	const { setPageTitle } = useContext(PacksContext);

	// Two shapes leave `isLoading` false with nothing confirmed: a malformed 200 body (no
	// error, but nothing usable either), and a network-level failure (a rejected request
	// with no HTTP status at all — DNS, timeout, reset). Both are unconfirmed, so treat them
	// as still-loading everywhere `isLoading` gates what's shown, not just the redirect
	// below. A genuine HTTP error — a status is present, including a confirmed 404 — falls
	// through instead: retrying forever behind a loader wouldn't help there, and `notFound`
	// below already decides what happens with the 404 case specifically.
	const effectiveIsLoading = isLoading || (!hasResponse && (!error || error?.status === undefined));

	// The better solution here would be to figure out the lifecycle of the above
	// loading state to see why occasionally isLoading is false when it should be true
	// and thus prematurely redirecting but I have to move on with my life so I'm
	// using a timeout - MB
	//
	// A feed is a 404 either because the server actively confirmed it (notFound) or because
	// a response that actually arrived came back empty. An empty `packs` on its own doesn't
	// distinguish "really empty" from "request never landed", and search opens this route
	// often enough that a transient failure is no longer a rare edge case — `error` alone
	// isn't enough, since the shared axios interceptor also rejects on a network-level
	// failure with no payload, which is exactly the case that would otherwise bounce a real
	// collection. `notFound` bypasses that caution because it's the opposite case: a
	// confirmed absence, not an unconfirmed failure.
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!effectiveIsLoading && (notFound || (!error && packs.length <= 0))) {
				routerPush('/404');
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [effectiveIsLoading, packs, error, notFound, routerPush]);

	useEffect(() => {
		if (feedTitle) {
			setPageTitle(feedTitle);
		}
	}, [feedTitle, setPageTitle]);

	if (!effectiveIsLoading && (notFound || (!error && packs.length <= 0))) {
		return null;
	}

	return (
		<Layout pageName={feedName}>
			<PacksList
				packFeedName={feedName}
				packs={packs}
				tags={tags}
				activeTag={tags[0]}
				isLoading={effectiveIsLoading}
			/>
		</Layout>
	);
};

export default PackFeed;

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState: {
				...initialState,
				theme: {
					webAppLayout: 'full',
				},
			},
		},
	};
};
